<template>
  <div class="container-fluid staff-accounts">
    <div class="row">
      <div class="col-sm-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between">
            <div class="header-title">
              <h4 class="card-title">{{ pageTitle }}</h4>
            </div>
          </div>
          <div class="card-body">
            <div v-if="denied" class="alert alert-warning">
              仅限代表账号使用(대표 계정만 사용할 수 있습니다)
            </div>
            <b-tabs v-else lazy content-class="pt-3">
              <b-tab title="账号管理(계정 관리)" active>
              <!-- 새 계정 -->
              <div class="staff-accounts__form mb-4">
                <h5 class="mb-2">新建账号(새 계정 만들기)</h5>
                <div class="d-flex flex-wrap align-items-center">
                  <div class="input-group staff-accounts__id mr-2 mb-2">
                    <div class="input-group-prepend">
                      <span class="input-group-text">{{ prefix }}@</span>
                    </div>
                    <input v-model.trim="form.id" class="form-control" placeholder="账号(아이디) 例: wang" maxlength="20" @input="form.id = form.id.toLowerCase()">
                  </div>
                  <input v-model="form.name" class="form-control staff-accounts__name mr-2 mb-2" placeholder="姓名(이름)" maxlength="30">
                  <input v-model="form.memo" class="form-control staff-accounts__memo mr-2 mb-2" placeholder="备注(메모)" maxlength="200">
                  <input v-model.trim="form.code" class="form-control staff-accounts__code mr-2 mb-2" placeholder="代号(담당자 코드) 例: FK" maxlength="6" @input="form.code = form.code.toUpperCase()">
                  <select v-model="form.tokenFrom" class="form-control staff-accounts__token mr-2 mb-2" title="1688授权(1688 연결)">
                    <option value="">不连接1688(1688 연결 안 함)</option>
                    <option v-for="s in tokenSources" :key="s.email" :value="s.email">1688授权与 {{ s.email }} 相同({{ s.email }} 와 같게)</option>
                  </select>
                  <button class="btn btn-primary mb-2" type="button" :disabled="busy || !idValid || !codeValid(form.code)" @click="createStaff">创建(생성)</button>
                </div>
                <small class="text-muted">账号只能使用2~20位小写英文字母和数字(아이디는 영문 소문자·숫자 2~20자). 初始密码由系统生成，只显示一次(초기 비밀번호는 시스템이 만들고 한 번만 보여줍니다).</small>
                <small class="d-block text-muted">代号: 英文·数字1~6位，不可重复。员工登录后，负责人栏会自动填入代号；箱子标签的负责人缩写需2位，建议用2位英文大写(담당자 코드: 영문·숫자 1~6자, 중복 불가. 직원이 로그인하면 담당자 칸에 자동으로 들어갑니다. 박스 라벨 이니셜은 2자라서 영문 대문자 2자를 권장합니다).</small>
              </div>

              <!-- 임시 비밀번호 1회 표시 -->
              <div v-if="issued" class="alert alert-success staff-accounts__issued">
                <div><b>{{ issued.email }}</b> — 初始密码(초기 비밀번호):</div>
                <div class="d-flex align-items-center mt-1">
                  <code class="staff-accounts__pw mr-2">{{ issued.tempPassword }}</code>
                  <button class="btn btn-sm btn-outline-secondary mr-2" type="button" @click="copyPw">{{ copied ? '已复制(복사됨)' : '复制(복사)' }}</button>
                  <button class="btn btn-sm btn-outline-dark" type="button" @click="issued = null">关闭(닫기)</button>
                </div>
                <small class="d-block mt-1">此密码关闭后无法再次查看。员工首次登录时必须修改密码(닫으면 다시 볼 수 없습니다. 직원은 첫 로그인 때 비밀번호를 바꿔야 합니다).</small>
                <small v-if="issued.tokenLinked === false" class="d-block text-muted mt-1">未连接1688授权(1688 연결 없이 만들었습니다)</small>
              </div>

              <!-- 목록 -->
              <!-- 분류 목록(직접 추가) -->
              <div class="staff-accounts__groups mb-3">
                <h5 class="mb-2">分类(분류)</h5>
                <div class="d-flex flex-wrap align-items-center">
                  <span v-for="g in groupDraft" :key="g" class="badge badge-light border mr-1 mb-1 staff-accounts__group-badge">
                    {{ g }}
                    <a href="#" class="ml-1 text-danger" title="删除(삭제)" @click.prevent="groupDraft = groupDraft.filter(x => x !== g)">×</a>
                  </span>
                  <input v-model.trim="newGroup" class="form-control form-control-sm staff-accounts__group-input mr-1 mb-1" maxlength="20"
                         placeholder="新分类 例: 质检组(새 분류)" @keyup.enter="addGroup">
                  <button class="btn btn-sm btn-outline-primary mr-1 mb-1" type="button" :disabled="!newGroup" @click="addGroup">添加(추가)</button>
                  <button class="btn btn-sm btn-primary mb-1" type="button" :disabled="busy || !groupsChanged" @click="saveGroups">保存分类(분류 저장)</button>
                </div>
                <small class="text-muted">删除分类不会改变已设置该分类的账号(분류를 지워도 이미 그 분류인 계정은 그대로입니다)</small>
              </div>

              <div class="d-flex flex-wrap align-items-center justify-content-between mb-2">
                <h5 class="mb-0">账号列表(계정 목록) <small class="text-muted">{{ filteredStaff.length }} / {{ staff.length }}</small></h5>
                <div class="d-flex align-items-center">
                  <button class="btn btn-sm btn-outline-secondary mr-1" type="button" :disabled="!hasFilter" @click="clearFilters">清除筛选(필터 지우기)</button>
                  <button class="btn btn-sm btn-outline-success mr-1" type="button" :disabled="!staff.length" @click="downloadExcel">下载Excel(엑셀 내려받기)</button>
                  <label class="btn btn-sm btn-outline-primary mb-0" :class="{ disabled: busy }">
                    上传Excel批量修改(엑셀 올려 일괄 수정)
                    <input ref="excelInput" type="file" accept=".xlsx,.xls" class="d-none" :disabled="busy" @change="uploadExcel">
                  </label>
                </div>
              </div>
              <small class="d-block text-muted mb-2">可修改: 姓名·备注·代号·分类。账号不可修改，状态·登录时间即使修改也不会反映(바꿀 수 있는 칸: 이름·메모·담당자 코드·분류. 아이디는 못 바꾸고, 상태·접속 시각은 고쳐도 반영되지 않습니다).</small>

              <!-- 엑셀 미리보기 — 확인을 눌러야 저장된다 -->
              <div v-if="bulk" class="alert staff-accounts__bulk" :class="bulk.errors.length ? 'alert-danger' : 'alert-info'">
                <div class="mb-1"><b>{{ bulk.fileName }}</b> —
                  <template v-if="bulk.errors.length">有 {{ bulk.errors.length }} 处错误，请修改后重新上传。全部不会保存(오류 {{ bulk.errors.length }}곳 — 고쳐서 다시 올려 주세요. 하나도 저장되지 않습니다)</template>
                  <template v-else>将修改 {{ bulk.changes.length }} 个账号，无变化 {{ bulk.unchanged }} 个(계정 {{ bulk.changes.length }}개를 바꿉니다, 변화 없음 {{ bulk.unchanged }}개)</template>
                </div>
                <table v-if="bulk.errors.length" class="table table-sm mb-2">
                  <thead><tr><th>第几条数据(몇 번째 데이터 줄)</th><th>账号(아이디)</th><th>原因(사유)</th></tr></thead>
                  <tbody>
                    <tr v-for="(e, i) in bulk.errors" :key="'e' + i"><td>{{ e.row || '-' }}</td><td>{{ e.email }}</td><td>{{ bulkErrorText(e) }}</td></tr>
                  </tbody>
                </table>
                <table v-else-if="bulk.changes.length" class="table table-sm mb-2">
                  <thead><tr><th>账号(아이디)</th><th>项目(항목)</th><th>修改前(전)</th><th>修改后(후)</th></tr></thead>
                  <tbody>
                    <template v-for="c in bulk.changes">
                      <tr v-for="k in Object.keys(c.after)" :key="c.email + k">
                        <td>{{ c.email }}</td><td>{{ fieldLabel(k) }}</td><td>{{ c.before[k] || '-' }}</td><td><b>{{ c.after[k] || '-' }}</b></td>
                      </tr>
                    </template>
                  </tbody>
                </table>
                <button v-if="!bulk.errors.length && bulk.changes.length" class="btn btn-sm btn-primary mr-1" type="button" :disabled="busy" @click="applyBulk">
                  确认修改 {{ bulk.changes.length }} 个账号(계정 {{ bulk.changes.length }}개 수정 확인)
                </button>
                <button class="btn btn-sm btn-outline-dark" type="button" :disabled="busy" @click="bulk = null">取消(취소)</button>
              </div>

              <div class="table-responsive">
                <table class="table table-sm table-bordered staff-accounts__table">
                  <thead>
                    <tr>
                      <th>账号(아이디)</th>
                      <th>姓名(이름)</th>
                      <th>备注(메모)</th>
                      <th>代号(담당자 코드)</th>
                      <th>分类(분류)</th>
                      <th>角色(역할)</th>
                      <th>状态(상태)</th>
                      <th>最近登录(최근 접속)</th>
                      <th>操作(작업)</th>
                    </tr>
                    <!-- 열 필터 -->
                    <tr class="staff-accounts__filter">
                      <th><input v-model.trim="filters.email" class="form-control form-control-sm" placeholder="筛选(필터)"></th>
                      <th><input v-model.trim="filters.name" class="form-control form-control-sm" placeholder="筛选(필터)"></th>
                      <th><input v-model.trim="filters.memo" class="form-control form-control-sm" placeholder="筛选(필터)"></th>
                      <th><input v-model.trim="filters.code" class="form-control form-control-sm" placeholder="筛选(필터)"></th>
                      <th>
                        <select v-model="filters.group" class="form-control form-control-sm">
                          <option value="">全部(전체)</option>
                          <option value="-">未分类(분류 없음)</option>
                          <option v-for="g in groupOptions" :key="g" :value="g">{{ g }}</option>
                        </select>
                      </th>
                      <th></th>
                      <th>
                        <select v-model="filters.status" class="form-control form-control-sm">
                          <option value="">全部(전체)</option>
                          <option value="on">启用(사용중)</option>
                          <option value="off">停用(비활성)</option>
                        </select>
                      </th>
                      <th></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="s in filteredStaff" :key="s.email">
                      <td>{{ s.email }}</td>
                      <template v-if="editing && editing.email === s.email">
                        <td><input v-model="editing.name" class="form-control form-control-sm" maxlength="30" placeholder="姓名(이름)"></td>
                        <td><input v-model="editing.memo" class="form-control form-control-sm" maxlength="200" placeholder="备注(메모)"></td>
                        <td>
                          <input v-model.trim="editing.code" class="form-control form-control-sm staff-accounts__code-cell" maxlength="6" placeholder="FK"
                                 :class="{ 'is-invalid': !codeValid(editing.code) }" @input="editing.code = editing.code.toUpperCase()">
                        </td>
                        <td>
                          <select v-model="editing.group" class="form-control form-control-sm">
                            <option value="">未分类(분류 없음)</option>
                            <option v-for="g in groupOptions" :key="g" :value="g">{{ g }}</option>
                          </select>
                        </td>
                        <td>
                          <label class="d-block mb-0 small"><input v-model="editing.roles" type="checkbox" value="leader"> 组长(조장)</label>
                          <label class="d-block mb-0 small"><input v-model="editing.roles" type="checkbox" value="handler"> 代办组(처리담당조)</label>
                        </td>
                      </template>
                      <template v-else>
                        <td>{{ s.name }}</td>
                        <td>{{ s.memo }}</td>
                        <td><b v-if="s.code">{{ s.code }}</b><span v-else class="text-muted">-</span></td>
                        <td>
                          <span v-if="s.group">{{ s.group }}<small v-if="groups.indexOf(s.group) === -1" class="text-warning d-block">不在分类列表中(분류 목록에 없음)</small></span>
                          <span v-else class="text-muted">-</span>
                        </td>
                        <td>
                          <span v-if="(s.roles || []).indexOf('leader') !== -1" class="badge badge-info mr-1">组长(조장)</span>
                          <span v-if="(s.roles || []).indexOf('handler') !== -1" class="badge badge-primary">代办组(처리담당조)</span>
                          <span v-if="!(s.roles || []).length" class="text-muted">-</span>
                        </td>
                      </template>
                      <td>
                        <span v-if="s.active" class="badge badge-success">启用(사용중)</span>
                        <span v-else class="badge badge-secondary">停用(비활성)</span>
                        <span v-if="s.mustChangePw" class="badge badge-warning ml-1">待改密码(비번변경 대기)</span>
                      </td>
                      <td>{{ formatAccess(s.accesshistory) }}</td>
                      <td>
                        <template v-if="editing && editing.email === s.email">
                          <button class="btn btn-sm btn-primary mr-1" type="button" :disabled="busy || !codeValid(editing.code)" @click="saveEdit">保存(저장)</button>
                          <button class="btn btn-sm btn-outline-dark mr-1" type="button" :disabled="busy" @click="editing = null">取消(취소)</button>
                        </template>
                        <button v-else class="btn btn-sm btn-outline-primary mr-1" type="button" :disabled="busy" @click="startEdit(s)">修改(수정)</button>
                        <button v-if="s.active" class="btn btn-sm btn-outline-danger mr-1" type="button" :disabled="busy" @click="setActive(s, false)">停用(비활성화)</button>
                        <button v-else class="btn btn-sm btn-outline-success mr-1" type="button" :disabled="busy" @click="setActive(s, true)">启用(재활성화)</button>
                        <button class="btn btn-sm btn-outline-secondary mr-1" type="button" :disabled="busy" @click="resetPassword(s)">重置密码(비번 초기화)</button>
                        <button class="btn btn-sm btn-danger" type="button" :disabled="busy" @click="deleteStaff(s)">删除(삭제)</button>
                      </td>
                    </tr>
                    <tr v-if="!filteredStaff.length">
                      <td colspan="9" class="text-center text-muted">{{ staff.length ? '没有符合筛选条件的账号(필터에 맞는 계정 없음)' : '暂无账号(계정 없음)' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- 이력 -->
              <h5 class="mb-2 mt-4">操作记录(작업 이력)</h5>
              <div class="table-responsive staff-accounts__history">
                <table class="table table-sm">
                  <thead>
                    <tr><th>时间(시각)</th><th>操作(작업)</th><th>对象(대상)</th><th>操作人(처리자)</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="(h, i) in history" :key="i">
                      <td>{{ formatTime(h.at) }}</td>
                      <td>{{ actionLabel(h.action) }}</td>
                      <td>{{ h.target }}</td>
                      <td>{{ h.actor }}</td>
                    </tr>
                    <tr v-if="!history.length">
                      <td colspan="4" class="text-center text-muted">暂无记录(이력 없음)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              </b-tab>
              <b-tab title="全部操作记录(전체 작업 기록)">
                <coupass-staff-activity :staff="staff" />
              </b-tab>
              <b-tab title="工作量统计(작업량 통계)">
                <coupass-staff-stats :groups="groups" />
              </b-tab>
            </b-tabs>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { coupassMenuTitle } from './coupassMenu';
import axios from 'axios';
import * as XLSX from 'xlsx';
import CoupassStaffActivity from './CoupassStaffActivity.vue';
import CoupassStaffStats from './CoupassStaffStats.vue';
import { staffToSheetRows, sheetRowsToUpdates, BULK_ERROR_TEXT } from './coupassStaffExcel';

const FIELD_LABELS = { name: '姓名(이름)', memo: '备注(메모)', code: '代号(담당자 코드)', group: '分类(분류)', roles: '角色(역할)' };
const SHEET_ERROR_TEXT = {
  no_header: '文件是空的(빈 파일입니다)',
  no_account_column: '找不到「账号(아이디)」列，请使用下载的Excel格式(「账号(아이디)」 열이 없습니다 — 내려받은 엑셀 형식을 써 주세요)',
  no_editable_column: '没有可修改的列(바꿀 수 있는 열이 없습니다)',
};

const ACTION_LABELS = {
  create: '创建(생성)',
  deactivate: '停用(비활성화)',
  activate: '启用(재활성화)',
  'reset-password': '重置密码(비번 초기화)',
  update: '修改信息(정보 수정)',
  delete: '删除(삭제)',
  groups: '修改分类(분류 수정)',
};

export default {
  name: 'CoupassStaffAccounts',
  components: { CoupassStaffActivity, CoupassStaffStats },
  data() {
    return {
      pageTitle: coupassMenuTitle('logis.staffaccounts'),
      prefix: 'whxingmao',
      staff: [],
      history: [],
      denied: false,
      busy: false,
      form: { id: '', name: '', memo: '', code: '', tokenFrom: '' },
      editing: null,  // { email, name, memo, code } — 목록에서 고치는 중인 한 줄
      issued: null,   // { email, tempPassword, tokenLinked } — 화면에 한 번만 보여준다
      copied: false,
      groups: [],       // 저장된 분류 목록
      groupDraft: [],   // 화면에서 고치는 중인 분류 목록(「保存分类」 를 눌러야 저장)
      newGroup: '',
      filters: { email: '', name: '', memo: '', code: '', group: '', status: '' },
      bulk: null,       // 엑셀 미리보기 { fileName, rows, changes, errors, unchanged }
    };
  },
  computed: {
    groupOptions() {
      return this.groups;
    },
    groupsChanged() {
      return this.groupDraft.join('\n') !== this.groups.join('\n');
    },
    hasFilter() {
      return Object.keys(this.filters).some(k => this.filters[k]);
    },
    filteredStaff() {
      const f = this.filters;
      const has = (v, q) => !q || String(v || '').toLowerCase().indexOf(q.toLowerCase()) !== -1;
      return this.staff.filter(s => has(s.email, f.email) && has(s.name, f.name) && has(s.memo, f.memo) && has(s.code, f.code)
        && (!f.group || (f.group === '-' ? !s.group : s.group === f.group))
        && (!f.status || (f.status === 'on' ? s.active : !s.active)));
    },
    idValid() {
      return /^[a-z0-9]{2,20}$/.test(this.form.id);
    },
    // 1688 연결을 복사해 올 수 있는 기존 직원 계정(연결 여부만 받는다 — 토큰 값은 화면에 오지 않는다)
    tokenSources() {
      return this.staff.filter((s) => s.active && s.hasToken);
    },
  },
  mounted() {
    this.load();
  },
  beforeDestroy() {
    this.issued = null;
  },
  methods: {
    async load() {
      try {
        const res = await axios.post('/api/coupass-staff/list', {});
        if (res.data && res.data.success) {
          this.staff = res.data.staff || [];
          this.history = res.data.history || [];
          if (res.data.prefix) this.prefix = res.data.prefix;
          this.groups = res.data.groups || [];
          this.groupDraft = this.groups.slice();
        }
      } catch (e) {
        console.error('직원 계정 목록 조회 실패:', e);
        if (e.response && e.response.status === 403) {
          this.denied = true;
        } else {
          alert(this.errorMessage(e, '查询失败(조회 실패)'));
        }
      }
    },
    errorMessage(e, fallback) {
      return (e && e.response && e.response.data && e.response.data.message) || fallback;
    },
    async createStaff() {
      if (!this.idValid || this.busy) return;
      this.busy = true;
      try {
        const res = await axios.post('/api/coupass-staff/create', {
          id: this.form.id,
          name: this.form.name,
          memo: this.form.memo,
          code: this.form.code,
          tokenFrom: this.form.tokenFrom,
        });
        if (res.data && res.data.success) {
          this.issued = { email: res.data.email, tempPassword: res.data.tempPassword, tokenLinked: res.data.tokenLinked };
          this.copied = false;
          this.form = { id: '', name: '', memo: '', code: '', tokenFrom: '' };
          await this.load();
        }
      } catch (e) {
        console.error('직원 계정 생성 실패:', e);
        alert(this.errorMessage(e, '创建失败(생성 실패)'));
      } finally {
        this.busy = false;
      }
    },
    // 代号: 빈 값(코드 없음) 또는 영문·숫자 1~6자 — 서버와 같은 규칙
    codeValid(code) {
      return !code || /^[A-Za-z0-9]{1,6}$/.test(code);
    },
    startEdit(s) {
      this.editing = { email: s.email, name: s.name || '', memo: s.memo || '', code: s.code || '', group: s.group || '', roles: (s.roles || []).slice() };
    },
    async saveEdit() {
      if (!this.editing || this.busy || !this.codeValid(this.editing.code)) return;
      this.busy = true;
      try {
        const e = this.editing;
        const body = { email: e.email, name: e.name, memo: e.memo, code: e.code };
        // 분류는 바꿨을 때만 보낸다 — 목록에서 지운 분류를 쓰던 계정을 이름만 고칠 때 막히지 않게
        const cur = this.staff.find(x => x.email === e.email);
        if (!cur || (cur.group || '') !== e.group) body.group = e.group;
        // 역할도 바꿨을 때만 보낸다
        if (!cur || (cur.roles || []).slice().sort().join() !== e.roles.slice().sort().join()) body.roles = e.roles;
        const res = await axios.post('/api/coupass-staff/update', body);
        if (res.data && res.data.success) {
          this.editing = null;
          await this.load();
        }
      } catch (e) {
        console.error('직원 정보 수정 실패:', e);
        alert(this.errorMessage(e, '保存失败(저장 실패)'));
      } finally {
        this.busy = false;
      }
    },
    async setActive(s, active) {
      const msg = active
        ? `${s.email} 启用该账号吗?(이 계정을 다시 사용하게 할까요?)`
        : `${s.email} 停用该账号吗? 登录将被立即中断。(이 계정을 비활성화할까요? 접속이 바로 끊깁니다.)`;
      if (!confirm(msg)) return;
      this.busy = true;
      try {
        await axios.post('/api/coupass-staff/set-active', { email: s.email, active: active });
        await this.load();
      } catch (e) {
        console.error('직원 계정 상태 변경 실패:', e);
        alert(this.errorMessage(e, '操作失败(처리 실패)'));
      } finally {
        this.busy = false;
      }
    },
    async resetPassword(s) {
      if (!confirm(`${s.email} 重置密码吗? 当前登录将被中断。(비밀번호를 초기화할까요? 지금 접속은 끊깁니다.)`)) return;
      this.busy = true;
      try {
        const res = await axios.post('/api/coupass-staff/reset-password', { email: s.email });
        if (res.data && res.data.success) {
          this.issued = { email: res.data.email, tempPassword: res.data.tempPassword };
          this.copied = false;
          await this.load();
        }
      } catch (e) {
        console.error('직원 비밀번호 초기화 실패:', e);
        alert(this.errorMessage(e, '重置失败(초기화 실패)'));
      } finally {
        this.busy = false;
      }
    },
    async deleteStaff(s) {
      if (!confirm(`${s.email} 删除该账号吗? 删除后无法恢复，该账号将无法登录。(이 계정을 삭제할까요? 삭제하면 되돌릴 수 없고, 이 계정으로 로그인할 수 없습니다.)`)) return;
      this.busy = true;
      try {
        await axios.post('/api/coupass-staff/delete', { email: s.email });
        if (this.editing && this.editing.email === s.email) this.editing = null;
        await this.load();
      } catch (e) {
        console.error('직원 계정 삭제 실패:', e);
        alert(this.errorMessage(e, '删除失败(삭제 실패)'));
      } finally {
        this.busy = false;
      }
    },
    async copyPw() {
      if (!this.issued) return;
      try {
        // 아이디와 비밀번호를 같이 복사해 직원에게 그대로 전달할 수 있게 한다(YZY 요청 2026-10-03)
        await navigator.clipboard.writeText(`账号(아이디): ${this.issued.email}
密码(비밀번호): ${this.issued.tempPassword}`);
        this.copied = true;
      } catch (e) {
        console.error('클립보드 복사 실패:', e);
        alert('复制失败，请手动复制(복사 실패 — 직접 복사해 주세요)');
      }
    },
    addGroup() {
      const g = (this.newGroup || '').trim();
      if (!g) return;
      if (this.groupDraft.indexOf(g) === -1) this.groupDraft.push(g);
      this.newGroup = '';
    },
    async saveGroups() {
      if (this.busy) return;
      this.busy = true;
      try {
        const res = await axios.post('/api/coupass-staff/groups', { groups: this.groupDraft });
        if (res.data && res.data.success) await this.load();
      } catch (e) {
        console.error('직원 분류 저장 실패:', e);
        alert(this.errorMessage(e, '保存失败(저장 실패)'));
      } finally {
        this.busy = false;
      }
    },
    clearFilters() {
      this.filters = { email: '', name: '', memo: '', code: '', group: '', status: '' };
    },
    downloadExcel() {
      // 지금 필터에 보이는 계정만 내려받는다
      const aoa = staffToSheetRows(this.filteredStaff,
        s => (s.active ? '启用(사용중)' : '停用(비활성)'), s => this.formatAccess(s.accesshistory));
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws['!cols'] = [{ wch: 24 }, { wch: 14 }, { wch: 30 }, { wch: 16 }, { wch: 14 }, { wch: 22 }, { wch: 26 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '子账号');
      const stamp = new Date(Date.now() + 8 * 3600e3).toISOString().slice(0, 10);
      XLSX.writeFile(wb, `子账号列表_${stamp}.xlsx`);
    },
    async uploadExcel(ev) {
      const file = ev.target.files && ev.target.files[0];
      ev.target.value = '';   // 같은 파일을 다시 골라도 change 가 뜨게
      if (!file || this.busy) return;
      this.busy = true;
      try {
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const aoa = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1, defval: '', raw: false });
        const parsed = sheetRowsToUpdates(aoa);
        if (parsed.error) {
          alert(SHEET_ERROR_TEXT[parsed.error] || parsed.error);
          return;
        }
        // 미리보기 — 서버가 계산한다(저장 안 함)
        const res = await axios.post('/api/coupass-staff/bulk-update', { dryRun: true, rows: parsed.rows });
        const d = res.data || {};
        this.bulk = { fileName: file.name, rows: parsed.rows, changes: d.changes || [], errors: d.errors || [], unchanged: d.unchanged || 0 };
      } catch (e) {
        console.error('엑셀 읽기·미리보기 실패:', e);
        alert(this.errorMessage(e, '读取Excel失败(엑셀을 읽지 못했습니다)'));
      } finally {
        this.busy = false;
      }
    },
    async applyBulk() {
      if (!this.bulk || this.busy) return;
      if (!confirm(`确认修改 ${this.bulk.changes.length} 个账号吗?(계정 ${this.bulk.changes.length}개를 수정할까요?)`)) return;
      this.busy = true;
      try {
        // 적용 때 서버가 다시 계산한다 — 그사이 다른 사람이 바꿨으면 그 결과로 다시 보여준다
        const res = await axios.post('/api/coupass-staff/bulk-update', { rows: this.bulk.rows });
        const d = res.data || {};
        if (d.success) {
          alert(`已修改 ${d.applied} 个账号(계정 ${d.applied}개를 수정했습니다)`);
          this.bulk = null;
          await this.load();
        } else {
          this.bulk = Object.assign({}, this.bulk, { changes: d.changes || [], errors: d.errors || [], unchanged: d.unchanged || 0 });
        }
      } catch (e) {
        console.error('엑셀 일괄수정 실패:', e);
        alert(this.errorMessage(e, '保存失败(저장 실패)'));
      } finally {
        this.busy = false;
      }
    },
    bulkErrorText(e) {
      const t = BULK_ERROR_TEXT[e.reason] || e.reason;
      return e.value ? `${t}: ${e.value}` : t;
    },
    fieldLabel(k) {
      return FIELD_LABELS[k] || k;
    },
    actionLabel(a) {
      return ACTION_LABELS[a] || a;
    },
    formatTime(t) {
      if (!t) return '';
      const d = new Date(t);
      return isNaN(d.getTime()) ? String(t) : d.toLocaleString();
    },
    formatAccess(t) {
      return t ? this.formatTime(t) : '-';
    },
  },
};
</script>

<style lang="scss" scoped>
.staff-accounts {
  &__id { max-width: 320px; }
  &__name { max-width: 160px; }
  &__memo { max-width: 260px; }
  &__code { max-width: 180px; }
  &__code-cell { width: 90px; }
  &__token { max-width: 320px; }
  &__pw {
    font-size: 1.1rem;
    padding: 2px 8px;
    user-select: all;
  }
  &__group-badge { font-size: 0.9rem; padding: 4px 8px; }
  &__group-input { max-width: 200px; }
  &__filter th { padding: 2px; font-weight: normal; }
  &__bulk .table { background: #fff; }
  &__history {
    max-height: 320px;
    overflow-y: auto;
  }
}
</style>
